import type { MissionOption } from "../api/types";
import { k } from "../i18n/i18n";
import type { Draft } from "./missionDraft";

/** Posture des Données initiales (demande d'Alexis du 20/10/2026, docs/25 §12, registre A97) : au-dessus de
 *  « Mission », elle filtre la liste des missions de la force sur celles de cette posture. Classement doctrinal de
 *  chaque mission rendu par le serveur (`MissionOption.posture`), postures proposées : les modes tactiques que la
 *  doctrine prévoit au niveau et qui ont une mission à son catalogue (Assistance : au corps). */
export type DossierPosture = "offensive" | "defensive" | "securisation" | "assistance";

export const POSTURE_ORDER: readonly DossierPosture[] = ["offensive", "defensive", "securisation", "assistance"];

export const POSTURE_LABELS: Record<DossierPosture, string> = {
  offensive: k("Offensif"),
  defensive: k("Défensif"),
  securisation: k("Sécurisation"),
  assistance: k("Assistance"),
};

/** Postures proposées pour la liste des missions de la force, dans l'ordre Offensif · Défensif · Sécurisation ·
 *  Assistance. */
export function availablePostures(missions: readonly MissionOption[]): DossierPosture[] {
  return POSTURE_ORDER.filter((p) => missions.some((m) => m.posture === p));
}

/** Posture d'une mission de la liste ; null hors classement (mission introuvable, CENTAC). */
export function postureOf(missions: readonly MissionOption[], name: string | null | undefined): DossierPosture | null {
  const found = missions.find((m) => m.name === name);
  return (found?.posture as DossierPosture | null | undefined) ?? null;
}

/** Missions de la liste « Mission » pour une posture (toutes sans posture choisie). */
export function missionsFor(missions: readonly MissionOption[], posture: DossierPosture | null): MissionOption[] {
  return posture ? missions.filter((m) => m.posture === posture) : [...missions];
}

/** Mission proposée d'office pour une posture : celle que le serveur désigne (`posture_default`), sinon la première. */
export function defaultMissionFor(missions: readonly MissionOption[], posture: DossierPosture): MissionOption | null {
  const of = missionsFor(missions, posture);
  return of.find((m) => m.posture_default) ?? of[0] ?? null;
}

/** Posture affichée : celle du brouillon, sinon celle de sa mission. */
export function draftPosture(draft: Draft, missions: readonly MissionOption[]): DossierPosture | null {
  return (draft.parameters.posture as DossierPosture | null | undefined) ?? postureOf(missions, draft.parameters.mission);
}

/** Choix d'une posture : la mission courante reste si elle est de cette posture ; sinon la mission par défaut de la
 *  posture la remplace (`replaced` : ancienne et nouvelle mission, pour l'avis). */
export function choosePosture(
  draft: Draft,
  posture: DossierPosture,
  missions: readonly MissionOption[],
): { draft: Draft; replaced: { from: string; to: string } | null } {
  const current = draft.parameters.mission;
  if (postureOf(missions, current) === posture) {
    return { draft: { ...draft, parameters: { ...draft.parameters, posture } }, replaced: null };
  }
  const next = defaultMissionFor(missions, posture);
  if (!next) return { draft, replaced: null };
  return {
    draft: { ...draft, parameters: { ...draft.parameters, posture, mission: next.name, mode_action: next.mode } },
    replaced: current ? { from: current, to: next.name } : null,
  };
}

/** Choix d'une mission : sa posture suit. */
export function chooseMission(draft: Draft, mission: MissionOption | undefined): Draft {
  return {
    ...draft,
    parameters: {
      ...draft.parameters,
      mission: mission?.name ?? null,
      mode_action: mission?.mode ?? null,
      posture: (mission?.posture as DossierPosture | null | undefined) ?? draft.parameters.posture ?? null,
    },
  };
}
